import { Container, FillGradient, Text } from 'pixi.js';
import {
  AnimationState,
  AnimationStateData,
  Skeleton,
} from '@esotericsoftware/spine-core';
import { Physics, Spine, AtlasAttachmentLoader, SkeletonJson } from '@esotericsoftware/spine-pixi-v8';
import { gameConfig } from '../config/gameConfig.js';
import { formatCurrencyCelebrationAmount } from '../lib/formatCurrency.js';
import { sound } from '../utils/soundManager.js';

/** @typedef {'big' | 'huge' | 'mega' | 'epic'} WinTier */

/**
 * Minimum win÷stake to run the celebration overlay (see {@link gameConfig.winCelebrationTestTiers}).
 * @returns {number}
 */
export function winCelebrationMinMultiple() {
  return gameConfig.winCelebrationTestTiers ? 1 : 5;
}

const TURBO_CLIP = {
  big: 'fastBigWin',
  huge: 'fastHugeWin',
  mega: 'fastMegaWin',
  epic: 'fastEpicWin',
};

/** Spine buildup clip → popup sound (synced when each clip starts). */
const BUILDUP_CLIP_SOUNDS = {
  startBigWin: 'bigWin',
  transitionHugeWin: 'hugeWin',
  transitionMegaWin: 'megaWin',
  transitionEpicWin: 'epicWin',
  fastBigWin: 'bigWin',
  fastHugeWin: 'hugeWin',
  fastMegaWin: 'megaWin',
  fastEpicWin: 'epicWin',
};

/**
 * Full tier plan (reference). Playback uses {@link BUILDUP_PLAN} until collect, then {@link tierEndClip}.
 * Track 0–2 = idle1–3; 3+ = main sequence. No `end*` before a `transition*` on the same track.
 * @type {Record<WinTier, Record<number, string[]>>}
 */
const NORMAL_PLAN = {
  big: {
    3: ['startBigWin'],
    4: ['endBigWin'],
  },
  huge: {
    3: ['startBigWin'],
    4: ['transitionHugeWin'],
    5: ['endHugeWin'],
  },
  mega: {
    3: ['startBigWin'],
    4: ['transitionHugeWin'],
    5: ['transitionMegaWin'],
    6: ['endMegaWin'],
  },
  epic: {
    3: ['startBigWin'],
    4: ['transitionHugeWin'],
    5: ['transitionMegaWin'],
    6: ['transitionEpicWin'],
    7: ['endEpicWin'],
  },
};

/**
 * @param {Record<number, string[]>} plan
 * @returns {Record<number, string[]>}
 */
function planWithoutEndClips(plan) {
  /** @type {Record<number, string[]>} */
  const out = {};
  for (const [tr, clips] of Object.entries(plan)) {
    const kept = clips.filter((n) => !n.startsWith('end'));
    if (kept.length) out[Number(tr)] = kept;
  }
  return out;
}

/** Start + tier transitions only; `end*` clips run on collect. */
const BUILDUP_PLAN = /** @type {Record<WinTier, Record<number, string[]>>} */ (
  Object.fromEntries(
    Object.keys(NORMAL_PLAN).map((tier) => [tier, planWithoutEndClips(NORMAL_PLAN[/** @type {WinTier} */ (tier)])]),
  )
);

/**
 * @param {WinTier} tier
 * @returns {{ track: number, clip: string } | null}
 */
function tierEndClip(tier) {
  const plan = NORMAL_PLAN[tier];
  const tracks = Object.keys(plan)
    .map(Number)
    .sort((a, b) => b - a);
  for (const tr of tracks) {
    const end = plan[tr].find((n) => n.startsWith('end'));
    if (end) return { track: tr, clip: end };
  }
  return null;
}

/**
 * @param {import('@esotericsoftware/spine-core').SkeletonData} data
 * @param {Record<number, string[]>} plan
 */
function planDurationSec(data, plan) {
  let total = 0;
  const tracks = Object.keys(plan)
    .map(Number)
    .sort((a, b) => a - b);
  for (const tr of tracks) {
    for (const name of plan[tr]) {
      total += clipDurationSeconds(data, name);
    }
  }
  return total;
}

/**
 * Payout multiple (win ÷ stake) → celebration tier.
 * Production: 5–10 big, 10–20 mega, 20+ epic (huge is a build-up clip inside mega/epic).
 * Test ({@link gameConfig.winCelebrationTestTiers}): 1–2 big, 2–5 huge, 5–10 mega, 10+ epic.
 * @param {number} multiple
 * @returns {WinTier | null}
 */
export function winMultipleToTier(multiple) {
  const m = Number(multiple);
  if (!Number.isFinite(m)) return null;

  if (gameConfig.winCelebrationTestTiers) {
    if (m < 1) return null;
    if (m >= 10) return 'epic';
    if (m >= 5) return 'mega';
    if (m >= 2) return 'huge';
    return 'big';
  }

  if (m < 5) return null;
  if (m >= 20) return 'epic';
  if (m >= 10) return 'mega';
  return 'big';
}

/** Cached skeleton data for reading clip durations (same atlas/json as the live Spine). */
let _celebrationSkeletonData = null;

/**
 * @param {Record<string, unknown> | null | undefined} spineAssets
 * @returns {import('@esotericsoftware/spine-core').SkeletonData | null}
 */
function getCelebrationSkeletonData(spineAssets) {
  if (_celebrationSkeletonData) return _celebrationSkeletonData;
  const row = spineAssets?.winCelebration;
  if (!row?.skeletonData || !row?.textureAtlas) return null;
  try {
    const attachmentLoader = new AtlasAttachmentLoader(row.textureAtlas);
    const skeletonJson = new SkeletonJson(attachmentLoader);
    _celebrationSkeletonData = skeletonJson.readSkeletonData(row.skeletonData);
    return _celebrationSkeletonData;
  } catch {
    return null;
  }
}

/**
 * @param {import('@esotericsoftware/spine-core').SkeletonData} data
 * @param {string} clipName
 */
function clipDurationSeconds(data, clipName) {
  const anim = data.findAnimation(clipName);
  return anim?.duration ?? 0;
}

/**
 * Total Spine sequence duration (seconds) for the celebration tier / turbo mode.
 * Used to sync last-win count-up with the overlay animation.
 * @param {Record<string, unknown> | null | undefined} spineAssets
 * @param {number} multiple win ÷ stake
 * @param {boolean} turbo
 * @returns {number}
 */
export function getCelebrationSequenceDurationSec(spineAssets, multiple, turbo) {
  const data = getCelebrationSkeletonData(spineAssets);
  const tier = winMultipleToTier(multiple);
  if (!data || !tier) return 3;

  if (turbo) {
    const clip = TURBO_CLIP[tier];
    const d = clipDurationSeconds(data, clip);
    return Math.max(0.05, d || 1);
  }

  const plan = BUILDUP_PLAN[tier];
  const total = planDurationSec(data, plan);
  return Math.max(0.05, total || 2);
}

/** Max fraction of the logical viewport used as the fit box (leave safe margins). */
const FIT_VIEWPORT_W = 0.86;
const FIT_VIEWPORT_H = 0.76;
/** Extra inset applied after fitting to the envelope (prevents edge clipping). */
const FIT_ENVELOPE_PAD = 0.96;
/** Vertical offset for the overlay anchor (fraction of logical screen height). Negative = slightly higher. */
const ROOT_OFFSET_Y_FRAC = -0.055;

/** CSS `box-shadow` Y offset at 100px design size (`0 4px 0 0 #FFBF00`); scaled in {@link WinCelebrationOverlay#_layoutAmountText}. */
const AMOUNT_BOX_SHADOW_OFFSET_Y = 4;
/** CSS `border` width at the 100px design size; scaled with font size in {@link WinCelebrationOverlay#_layoutAmountText}. */
const AMOUNT_BORDER_STROKE_PX = 3;
/**
 * Win amount only: vertical position along the Spine local bounds — **must stay between 0 and 1**.
 * `0` = top of box, `1` = bottom. This is a **fraction**, not pixels (do not add large numbers here).
 */
const AMOUNT_ANCHOR_Y_FRAC = 0.78;
/**
 * Extra nudge **after** layout, in **root-local pixels** (same space as the slot layout). Positive = down.
 * Use this when you want “a bit lower” in screen space instead of changing the fraction.
 */
const AMOUNT_EXTRA_OFFSET_Y = 50;

/**
 * Spine win celebration: `public/assets/spines/winning_animations/`.
 * Root is placed slightly above logical viewport center ({@link setScreenCenterLayout}); skeleton is centered on that point.
 * Idles on tracks 0–2 loop; all other clips are one-shot.
 */
export class WinCelebrationOverlay {
  /**
   * @param {Record<string, unknown>} spineAssets
   */
  constructor(spineAssets) {
    this._assets = spineAssets;
    /** Scale box for the popup (logical px, updated in {@link setScreenCenterLayout}). */
    this._layout = { maxW: 900, maxH: 700, screenW: 1922, screenH: 1080 };
    /** @type {{ minX: number, minY: number, maxX: number, maxY: number } | null} */
    this._animationEnvelope = null;
    this._gen = 0;
    /** @type {WinTier | null} */
    this._activeTier = null;
    this._activeTurbo = false;
    this._endListener = null;
    /** In-flight `endXxxWin` Promise; reused so re-entry never restarts or aborts the end clip. @type {Promise<void> | null} */
    this._skipPromise = null;
    /** @type {number | null} */
    this._amountRollupRaf = null;
    this._currencyCode = 'FUN';
    this._currencyPrecision = 2;
    /** @type {string | undefined} */
    this._currencySymbol = undefined;
    /**
     * Uniform scale for Spine + win amount only (child of {@link #root}).
     * Lets GameApp shrink the celebration on small portrait phones without touching aspect compensation on {@link #root}.
     */
    this._celebrationContentScale = 1;
    /** Portrait canvas `scaleY/scaleX`; applied on {@link #_celebrationContent} only (root stays 1,1). */
    this._canvasStretchMulX = 1;
    /** Extra root Y in logical px ({@link #setCelebrationRootExtraOffsetYPx}); positive = lower on screen. */
    this._extraRootOffsetY = 0;

    this.root = new Container();
    this.root.visible = false;
    this.root.eventMode = 'none';
    this.root.pivot.set(0, 0);

    this._celebrationContent = new Container();
    this._celebrationContent.eventMode = 'none';
    this.root.addChild(this._celebrationContent);

    this._winFillGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 1 },
      end: { x: 0, y: 0 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: '#0400FF' },
        { offset: 0.7477, color: '#FF2ACA' },
        { offset: 1, color: '#FF2ACA' },
      ],
    });

    /** CSS `border-image`: linear-gradient(0deg, #40FF00 20.25%, #FFFFFF 100%) — green band then fade to white (bottom→top). */
    this._amountBorderGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 1 },
      end: { x: 0, y: 0 },
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color: '#40FF00' },
        { offset: 0.2025, color: '#40FF00' },
        { offset: 1, color: '#FFFFFF' },
      ],
    });

    this._amountLayer = new Container();
    this._amountLayer.eventMode = 'none';
    const mainStyle = {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: '700',
      fontSize: 100,
      lineHeight: 122,
      fill: this._winFillGradient,
      stroke: {
        width: AMOUNT_BORDER_STROKE_PX,
        fill: this._amountBorderGradient,
      },
      /** `box-shadow: 0px 4px 0px 0px #FFBF00` — canvas shadow pass, blur 0 */
      dropShadow: {
        alpha: 1,
        angle: Math.PI / 2,
        blur: 0,
        color: '#FFBF00',
        distance: AMOUNT_BOX_SHADOW_OFFSET_Y,
      },
    };
    this._amountMain = new Text({ text: '', style: mainStyle });
    this._amountMain.anchor.set(0.5);
    this._amountLayer.addChild(this._amountMain);

    this._spine = this._createSpine();
    if (this._spine) {
      this._fitSpine();
      this._celebrationContent.addChild(this._spine);
    }
    /** Sibling of Spine (not a child): same layout as before — only the label position math changed. */
    this._celebrationContent.addChild(this._amountLayer);
  }

  /**
   * @param {{ currencyCode?: string, currencyPrecision?: number, currencySymbol?: string }} [opts]
   */
  setCurrency(opts = {}) {
    if (opts.currencyCode != null && String(opts.currencyCode).trim() !== '') {
      this._currencyCode = String(opts.currencyCode).trim().toUpperCase();
    }
    if (Number.isFinite(Number(opts.currencyPrecision))) {
      this._currencyPrecision = Number(opts.currencyPrecision);
    }
    const sym = opts.currencySymbol;
    this._currencySymbol =
      sym == null || String(sym).trim() === '' ? undefined : String(sym).trim();
  }

  _formatCelebrationWinAmount(amount) {
    if (gameConfig.winCelebrationFixedTestAmount) {
      return '$123.456.07';
    }
    return formatCurrencyCelebrationAmount(
      amount,
      this._currencyCode,
      this._currencyPrecision,
      this._currencySymbol,
    );
  }

  /**
   * Shrink or grow the Spine + amount block uniformly (default 1). Used on narrow portrait where reels are scaled down but this overlay is not.
   * @param {number} mul clamped to ~0.35–1.38
   */
  setCelebrationContentScale(mul) {
    const m = Math.max(0.35, Math.min(1.38, Number(mul) || 1));
    this._celebrationContentScale = m;
    this._applyCelebrationContentScale();
  }

  /** @param {number} mulX portrait canvas `scaleY/scaleX`, else `1` — call before {@link #setScreenCenterLayout}. */
  setCanvasStretchMulX(mulX) {
    const x = Number(mulX);
    this._canvasStretchMulX = Number.isFinite(x) && x > 0 ? Math.max(0.25, Math.min(4, x)) : 1;
  }

  _applyCelebrationContentScale() {
    const m = this._celebrationContentScale;
    const sx = m * this._canvasStretchMulX;
    this._celebrationContent.scale.set(sx, m);
    this._layoutAmountText();
  }

  /**
   * Place the overlay at the logical viewport center (same space as {@link SlotMachine#layoutScreen})
   * and size the animation to fit the viewport.
   * @param {number} screenW
   * @param {number} screenH
   */
  setScreenCenterLayout(screenW, screenH) {
    const sw = Math.max(1, Number(screenW) || 1);
    const sh = Math.max(1, Number(screenH) || 1);
    this._layout.screenW = sw;
    this._layout.screenH = sh;
    this._layout.maxW = sw * FIT_VIEWPORT_W;
    this._layout.maxH = sh * FIT_VIEWPORT_H;
    this.root.scale.set(1, 1);
    this._celebrationContent.position.set(0, 0);
    this.root.position.set(sw / 2, sh / 2 + sh * ROOT_OFFSET_Y_FRAC + this._extraRootOffsetY);
    this._fitSpine();
    this._layoutAmountText();
  }

  /**
   * Nudges the celebration anchor vertically (e.g. tablet). Cleared when not used; call with 0 to reset.
   * @param {number} px logical screen px; positive moves the animation lower.
   */
  setCelebrationRootExtraOffsetYPx(px) {
    this._extraRootOffsetY = Number(px) || 0;
    const sw = this._layout.screenW ?? 0;
    const sh = this._layout.screenH ?? 0;
    if (sw >= 1 && sh >= 1) {
      this.root.position.set(sw / 2, sh / 2 + sh * ROOT_OFFSET_Y_FRAC + this._extraRootOffsetY);
    }
  }

  /**
   * Positions only the win amount (sibling of Spine under {@link #root}).
   * Uses the Spine **parent-space** AABB from {@link Spine#getBounds} — same coordinate space as
   * `root`, so no `toLocal` chain. `getLocalBounds()` on Spine is often degenerate for meshes,
   * which previously forced a fixed fallback and made `AMOUNT_*` constants appear to do nothing.
   */
  _layoutAmountText() {
    if (!this._spine || !this._amountLayer) return;
    const sw = this._layout.screenW ?? 1922;
    const fs = Math.round(Math.min(100, Math.max(44, sw * 0.052)));
    const lh = Math.round(fs * 1.22);
    this._amountMain.style.fontSize = fs;
    this._amountMain.style.lineHeight = lh;
    const borderW = Math.max(1, Math.round((AMOUNT_BORDER_STROKE_PX * fs) / 100));
    const shadowDist = Math.max(1, Math.round((AMOUNT_BOX_SHADOW_OFFSET_Y * fs) / 100));
    this._amountMain.style.stroke = {
      width: borderW,
      fill: this._amountBorderGradient,
    };
    this._amountMain.style.dropShadow = {
      alpha: 1,
      angle: Math.PI / 2,
      blur: 0,
      color: '#FFBF00',
      distance: shadowDist,
    };

    if (this._spine.skeleton) {
      this._spine.skeleton.updateWorldTransform(Physics.update);
    }
    const b = this._spine.getBounds(false);
    const h = b.maxY - b.minY;
    const w = b.maxX - b.minX;
    if (!Number.isFinite(h) || !Number.isFinite(w) || h <= 0 || w <= 0 || b.isEmpty()) {
      this._amountLayer.position.set(0, 250);
      return;
    }
    const frac = Math.min(1, Math.max(0, AMOUNT_ANCHOR_Y_FRAC));
    const cx = b.minX + w * 0.5;
    const cy = b.minY + h * frac;
    this._amountLayer.position.set(cx, cy + AMOUNT_EXTRA_OFFSET_Y);
  }

  _createSpine() {
    const res = this._assets?.winCelebration;
    if (!res?.skeletonData || !res?.textureAtlas) return null;
    try {
      const attachmentLoader = new AtlasAttachmentLoader(res.textureAtlas);
      const skeletonJson = new SkeletonJson(attachmentLoader);
      const skeleton = skeletonJson.readSkeletonData(res.skeletonData);
      const spine = new Spine(skeleton);
      spine.skeleton.setToSetupPose();
      spine.skeleton.updateWorldTransform(Physics.update);
      return spine;
    } catch (e) {
      console.warn('WinCelebrationOverlay: failed to create spine', e);
      return null;
    }
  }

  /**
   * Union of skeleton attachment bounds across every animation (sampled over time).
   * Setup-pose DisplayObject bounds are often far too small, which over-scales the popup.
   * @returns {{ minX: number, minY: number, maxX: number, maxY: number } | null}
   */
  _computeAnimationEnvelope() {
    const data = this._spine?.skeleton?.data;
    if (!data?.animations?.length) return null;

    const stateData = new AnimationStateData(data);
    const asm = new AnimationState(stateData);
    const sk = new Skeleton(data);

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    const merge = (r) => {
      if (!r || r.width === Number.NEGATIVE_INFINITY || !Number.isFinite(r.x)) return;
      minX = Math.min(minX, r.x);
      minY = Math.min(minY, r.y);
      maxX = Math.max(maxX, r.x + r.width);
      maxY = Math.max(maxY, r.y + r.height);
    };

    sk.setToSetupPose();
    sk.updateWorldTransform(Physics.update);
    merge(sk.getBoundsRect());

    for (let ai = 0; ai < data.animations.length; ai++) {
      const anim = data.animations[ai];
      if (!anim?.duration || anim.duration <= 0) continue;

      sk.setToSetupPose();
      asm.clearTracks();
      asm.setAnimation(0, anim.name, false);

      const duration = anim.duration;
      const timeStep = Math.max(duration / 24, 1 / 30);
      const steps = Math.max(Math.ceil(duration / timeStep), 1);
      for (let i = 0; i <= steps; i++) {
        const delta = i > 0 ? timeStep : 0;
        asm.update(delta);
        asm.apply(sk);
        sk.update(delta);
        sk.updateWorldTransform(Physics.update);
        merge(sk.getBoundsRect());
      }
    }

    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return null;
    return { minX, minY, maxX, maxY };
  }

  _fitSpine() {
    const spine = this._spine;
    if (!spine?.skeleton) return;
    const maxW = Math.max(120, this._layout.maxW);
    const maxH = Math.max(120, this._layout.maxH);

    if (!this._animationEnvelope) {
      this._animationEnvelope = this._computeAnimationEnvelope();
    }
    const env = this._animationEnvelope;

    spine.scale.set(1);
    spine.x = 0;
    spine.y = 0;
    spine.skeleton.setToSetupPose();
    spine.skeleton.updateWorldTransform(Physics.update);

    let bw;
    let bh;

    if (env) {
      bw = Math.max(1, env.maxX - env.minX);
      bh = Math.max(1, env.maxY - env.minY);
    } else {
      const b = spine.getBounds();
      bw = Math.max(1, b.width);
      bh = Math.max(1, b.height);
    }

    const sRaw = Math.min(maxW / bw, maxH / bh) * FIT_ENVELOPE_PAD;
    const s = Number.isFinite(sRaw) && sRaw > 0 ? sRaw : 1;
    spine.scale.set(s);
    spine.x = 0;
    spine.y = 0;
    spine.skeleton.setToSetupPose();
    spine.skeleton.updateWorldTransform(Physics.update);

    const b2 = spine.getBounds();
    spine.x = -(b2.x + b2.width * 0.5);
    spine.y = -(b2.y + b2.height * 0.5);
    this._layoutAmountText();
  }

  _stopAmountRollup() {
    if (this._amountRollupRaf != null) {
      cancelAnimationFrame(this._amountRollupRaf);
      this._amountRollupRaf = null;
    }
  }

  /**
   * Count up the overlay amount over `durationSec` (synced with the Spine build-up length).
   * Uses the same linear ramp idea as {@link GameApp#_animateLastWinRollup}.
   */
  _startWinAmountRollup(gen, targetWin, durationSec, immediate) {
    this._stopAmountRollup();
    const target = Math.max(0, Number(targetWin) || 0);

    const applyText = (shown) => {
      const t = this._formatCelebrationWinAmount(shown);
      this._amountMain.text = t;
    };

    if (immediate || target <= 0) {
      applyText(target);
      return;
    }

    const sec = Math.max(0.05, Number(durationSec) || 0.05);
    const startTs = performance.now();
    const tick = () => {
      if (gen !== this._gen) return;
      const elapsed = (performance.now() - startTs) / 1000;
      const shown = Math.min(target, (elapsed / sec) * target);
      applyText(shown);
      if (shown >= target) {
        this._amountRollupRaf = null;
        return;
      }
      this._amountRollupRaf = requestAnimationFrame(tick);
    };
    applyText(0);
    this._amountRollupRaf = requestAnimationFrame(tick);
  }

  /**
   * Park build-up tracks at their final frame so tier attachments are visible (collect / resume).
   * @param {WinTier} tier
   * @param {boolean} [turbo]
   */
  _applyBuildupFinalPose(tier, turbo = this._activeTurbo) {
    const state = this._spine?.state;
    const skeletonData = this._spine?.skeleton?.data;
    if (!state || !skeletonData) return;

    if (turbo) {
      const fastClipName = TURBO_CLIP[tier];
      const fastAnim = fastClipName ? skeletonData.findAnimation(fastClipName) : null;
      if (fastAnim) {
        const fastTrack = state.tracks[3];
        const alreadyAtEnd =
          fastTrack &&
          fastTrack.animation?.name === fastClipName &&
          fastTrack.trackTime >= fastTrack.animationEnd;
        if (!alreadyAtEnd) {
          const e = state.setAnimation(3, fastClipName, false);
          if (e) {
            e.mixDuration = 0;
            e.trackTime = e.animationEnd;
          }
        }
      }
      return;
    }

    const plan = BUILDUP_PLAN[tier];
    const buildupTracks = Object.keys(plan)
      .map(Number)
      .sort((a, b) => a - b);
    for (const tr of buildupTracks) {
      const clips = plan[tr];
      const lastClipName = clips[clips.length - 1];
      const anim = skeletonData.findAnimation(lastClipName);
      if (!anim) continue;
      const currentTrack = state.tracks[tr];
      const alreadyAtEnd =
        currentTrack &&
        currentTrack.animation?.name === lastClipName &&
        currentTrack.trackTime >= currentTrack.animationEnd;
      if (!alreadyAtEnd) {
        const e = state.setAnimation(tr, lastClipName, false);
        if (e) {
          e.mixDuration = 0;
          e.trackTime = e.animationEnd;
        }
      }
    }
  }

  /**
   * Show the tier popup at its post-buildup hold pose (no replay) — used when resuming after
   * the player already saw the celebration but has not collected yet.
   * @param {number} multiple win ÷ stake
   * @param {number} totalWinAmount
   */
  showStaticHold(multiple, totalWinAmount) {
    const tier = winMultipleToTier(multiple);
    if (!tier || !this._spine?.state) return;

    const gen = ++this._gen;
    const state = this._spine.state;
    this._detachEndListener();
    this._stopAmountRollup();

    this.root.visible = true;
    state.clearTracks();
    this._spine.skeleton.setToSetupPose();
    this._layoutAmountText();

    this._activeTier = tier;
    this._activeTurbo = false;

    state.setAnimation(0, 'idle1', true);
    state.setAnimation(1, 'idle2', true);
    state.setAnimation(2, 'idle3', true);
    this._applyBuildupFinalPose(tier, false);
    this._startWinAmountRollup(gen, totalWinAmount, 0, true);
  }

  /**
   * Resume after session interrupt: continue buildup when mid-animation, static hold when buildup finished.
   * @param {number} multiple win ÷ stake
   * @param {number} totalWinAmount
   * @param {number} elapsedSec seconds since celebration started
   * @param {() => void} [onBuildupComplete]
   */
  playContinue(multiple, totalWinAmount, elapsedSec, onBuildupComplete) {
    const tier = winMultipleToTier(multiple);
    if (!tier || !this._spine?.state) return;

    const seqSec = getCelebrationSequenceDurationSec(this._assets, multiple, false);
    const elapsed = Math.max(0, Number(elapsedSec) || 0);
    if (elapsed >= Math.max(0.05, seqSec * 0.95)) {
      this.showStaticHold(multiple, totalWinAmount);
      onBuildupComplete?.();
      return;
    }

    this.play(multiple, false, totalWinAmount, undefined, onBuildupComplete);
  }

  /**
   * @param {number} multiple win ÷ stake
   * @param {boolean} turbo
   * @param {number} totalWinAmount
   * @param {(finished: boolean) => void} [onDone] `true` if the full sequence finished (not skipped).
   * @param {() => void} [onBuildupComplete] Fired when tier build-up finishes and the popup waits for collect.
   */
  play(multiple, turbo, totalWinAmount, onDone, onBuildupComplete) {
    const tier = winMultipleToTier(multiple);
    if (!tier || !this._spine?.state) {
      onDone?.(false);
      return;
    }

    const gen = ++this._gen;
    const state = this._spine.state;
    this._detachEndListener();
    this._stopAmountRollup();

    this.root.visible = true;
    state.clearTracks();
    this._spine.skeleton.setToSetupPose();
    this._layoutAmountText();

    const seqSec = getCelebrationSequenceDurationSec(this._assets, multiple, turbo);
    this._startWinAmountRollup(gen, totalWinAmount, seqSec, false);

    const done = (finished) => {
      if (gen !== this._gen) return;
      onDone?.(!!finished);
    };

    this._activeTier = tier;
    /* Turbo reel snap must not swap in fast* clips — they finish and clear slot
     * attachments without the tier end* close, which looks like a forced hide. */
    this._activeTurbo = false;

    const buildupDone = () => {
      if (gen !== this._gen) return;
      /* Stay visible until collect; user tap runs {@link #skip} → tier end clip. */
      onBuildupComplete?.();
    };

    state.setAnimation(0, 'idle1', true);
    state.setAnimation(1, 'idle2', true);
    state.setAnimation(2, 'idle3', true);

    const plan = BUILDUP_PLAN[tier];
    const trackIndices = Object.keys(plan)
      .map(Number)
      .sort((a, b) => a - b);

    const playChain = (trackIdxPos) => {
      if (gen !== this._gen) return;
      if (trackIdxPos >= trackIndices.length) {
        buildupDone();
        return;
      }
      const tr = trackIndices[trackIdxPos];
      const clips = plan[tr];
      this._playSequentialOnTrack(gen, tr, clips, () => playChain(trackIdxPos + 1));
    };

    playChain(0);
  }

  /**
   * Queue one-shot clips on a track so each runs full length (Spine {@link AnimationState#addAnimation}).
   * @param {number} track
   * @param {string[]} clips
   * @returns {import('@esotericsoftware/spine-core').TrackEntry | null}
   */
  _queueClipsOnTrack(track, clips) {
    const state = this._spine?.state;
    if (!state || !clips.length) return null;

    /** @type {import('@esotericsoftware/spine-core').TrackEntry | null} */
    let lastEntry = null;
    for (let i = 0; i < clips.length; i++) {
      const name = clips[i];
      const entry =
        i === 0
          ? state.setAnimation(track, name, false)
          : state.addAnimation(track, name, false, 0);
      if (entry) {
        entry.mixDuration = 0;
        lastEntry = entry;
      }
    }
    return lastEntry;
  }

  /**
   * @param {number} gen
   * @param {number} track
   * @param {string[]} clips
   * @param {() => void} onTrackDone
   */
  _playSequentialOnTrack(gen, track, clips, onTrackDone) {
    const state = this._spine?.state;
    if (!state || gen !== this._gen || !clips.length) {
      onTrackDone();
      return;
    }

    const names = [];
    for (const name of clips) {
      if (this._spine.skeleton.data.findAnimation(name)) {
        names.push(name);
      } else {
        console.warn(`WinCelebrationOverlay: missing animation "${name}"`);
      }
    }
    if (!names.length) {
      onTrackDone();
      return;
    }

    for (let i = 0; i < names.length; i++) {
      const snd = BUILDUP_CLIP_SOUNDS[names[i]];
      if (snd) sound.playWinCelebrationTierSound(snd);
    }

    const lastEntry = this._queueClipsOnTrack(track, names);
    this._armComplete(gen, lastEntry, onTrackDone);
  }

  /**
   * @param {number} gen
   * @param {import('@esotericsoftware/spine-core').TrackEntry | null} entry
   * @param {() => void} onComplete
   */
  _armComplete(gen, entry, onComplete) {
    if (!entry) {
      onComplete();
      return;
    }
    entry.listener = {
      complete: (e) => {
        if (gen !== this._gen) return;
        if (e.trackIndex !== entry.trackIndex) return;
        onComplete();
      },
    };
  }

  _detachEndListener() {
    this._endListener = null;
  }

  /** Hide and stop; used after the end clip or when aborting (new spin). */
  reset() {
    this._gen += 1;
    sound.stopWinCelebrationSounds();
    this._activeTier = null;
    this._activeTurbo = false;
    this._skipPromise = null;
    this._stopAmountRollup();
    this._detachEndListener();
    if (this._amountMain) this._amountMain.text = '';
    if (this._spine?.state) {
      try {
        this._spine.state.clearTracks();
      } catch {
        /* ignore */
      }
    }
    this.root.visible = false;
  }

  /**
   * Collect tap: play this tier's `end*` clip (e.g. `endHugeWin`), then hide.
   * Idempotent: re-entry while the end clip is already playing returns the same in-flight promise
   * so the `endXxxWin` never restarts or aborts.
   * @returns {Promise<void>}
   */
  skip() {
    if (this._skipPromise) return this._skipPromise;

    if (!this.root.visible || !this._spine?.state || !this._activeTier) {
      this.reset();
      return Promise.resolve();
    }

    const tier = this._activeTier;
    const end = tierEndClip(tier);
    if (!end || !this._spine.skeleton.data.findAnimation(end.clip)) {
      this.reset();
      return Promise.resolve();
    }

    const gen = ++this._gen;
    const state = this._spine.state;
    sound.stopWinCelebrationSounds();
    this._detachEndListener();
    this._stopAmountRollup();

    /* Instantly complete any unfinished build-up tracks so the tier's full
     * visual state is set up before the end clip plays. Without this, an
     * early collect tap (e.g. during startBigWin for a huge win, or during a
     * turbo fast clip) causes the end clip to animate bones whose slot
     * attachments were never populated — making the close animation invisible.
     *
     * For TURBO: a single fast clip on track 3 covers the full tier display.
     * For NORMAL: BUILDUP_PLAN tracks (startBigWin → transitionXxxWin …) each
     * carry slot attachments that the corresponding end clip requires.
     *
     * CRITICAL: do NOT clear tracks 3..(end.track-1). Those build-up clips
     * (startBigWin, transitionHugeWin, …) own the popup's SLOT ATTACHMENTS
     * (board, glow, flowers, text, etc.). Clearing them reverts slots to the
     * setup pose, which has no attachments, so the popup goes invisible while
     * the end clip animates an unseen bone — looking like an instant disappear.
     * The end clip is designed to layer on a higher track and animate ONLY the
     * tier's master bone (e.g. `Big Win master` 1 → 1.139 → 0); the build-up
     * tracks stay parked at their final frame so the slots remain populated. */
    this._applyBuildupFinalPose(tier);

    const entry = state.setAnimation(end.track, end.clip, false);
    if (entry) entry.mixDuration = 0;
    sound.play('popupOut');

    const clipSec = clipDurationSeconds(this._spine.skeleton.data, end.clip);
    const timeoutMs = Math.min(8000, Math.max(1200, (clipSec + 0.75) * 1000));

    const promise = new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        if (gen !== this._gen) {
          resolve();
          return;
        }
        this.reset();
        resolve();
      };
      const timeoutId = setTimeout(() => {
        if (gen !== this._gen) {
          finish();
          return;
        }
        console.warn('WinCelebrationOverlay: skip end clip timed out, forcing reset');
        finish();
      }, timeoutMs);
      this._armComplete(gen, entry, finish);
    }).finally(() => {
      if (this._skipPromise === promise) this._skipPromise = null;
    });
    this._skipPromise = promise;
    return promise;
  }
}
